import React, { useState, useEffect, useCallback } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Activity, Pill, Droplets, ListTodo, Brain, FileText, AlertTriangle,
  ChevronRight, ChevronLeft, Send, Loader2, Clock, Shield, MessageSquare
} from 'lucide-react';

const ICONS = { activity: Activity, pill: Pill, droplets: Droplets, list: ListTodo, brain: Brain, file: FileText, alert: AlertTriangle };
const TRIAGE_COLORS = { urgent: '#C85C5C', clinical_priority: '#C87B7B', same_day: '#D4A843', routine: '#4E7A6B' };

export default function CareSupport() {
  const { user } = useAuth();
  const [view, setView] = useState('home'); // home | form | thread
  const [categories, setCategories] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formSchema, setFormSchema] = useState([]);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [redFlagResult, setRedFlagResult] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [catRes, tickRes] = await Promise.all([
        axios.get(`${API}/support-v2/categories`),
        axios.get(`${API}/support-v2/tickets/my`),
      ]);
      setCategories(catRes.data?.categories || []);
      setTickets(tickRes.data?.tickets || []);
      // Fetch notifications
      const notifRes = await axios.get(`${API}/support-v2/notifications`).catch(() => ({ data: {} }));
      setUnreadCount(notifRes.data?.unread_count || 0);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectCategory = async (cat) => {
    setSelectedCategory(cat);
    setFormData({});
    setMessage('');
    try {
      const res = await axios.get(`${API}/support-v2/form-schema/${cat.key}`);
      setFormSchema(res.data?.fields || []);
    } catch { setFormSchema([]); }
    setView('form');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/support-v2/tickets`, {
        category: selectedCategory.key,
        structured_payload: formData,
        message: message,
        priority: formData.severity >= 8 ? 'high' : 'normal',
      });
      if (res.data.red_flag_alert) {
        setRedFlagResult(res.data);
      } else {
        toast.success('Support request submitted — your care team has been notified');
      }
      setView('home');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const openThread = async (ticket) => {
    try {
      const res = await axios.get(`${API}/support-v2/tickets/${ticket.id}`);
      setActiveTicket(res.data.ticket);
      setThreadMessages(res.data.messages || []);
      setView('thread');
    } catch { toast.error('Failed to load'); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeTicket) return;
    try {
      await axios.post(`${API}/support-v2/tickets/${activeTicket.id}/message`, { message: replyText });
      setReplyText('');
      const res = await axios.get(`${API}/support-v2/tickets/${activeTicket.id}`);
      setThreadMessages(res.data.messages || []);
    } catch { toast.error('Failed to send'); }
  };

  if (loading) return <BiologicalLayout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div></BiologicalLayout>;

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="care-support">
        {/* Red flag alert */}
        {redFlagResult?.red_flag_alert && (
          <div className="mb-6 p-5 rounded-2xl bg-[#C85C5C]/10 border border-[#C85C5C]/20" data-testid="red-flag-alert">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-6 h-6 text-[#C85C5C]" />
              <h2 className="text-base font-['Outfit'] text-[#C85C5C]">Urgent Alert Sent</h2>
            </div>
            <p className="text-sm text-[#C85C5C] font-['Work_Sans']">{redFlagResult.red_flag_message}</p>
            <p className="text-xs text-[#C85C5C]/60 font-['Work_Sans'] mt-2">Your entire care team has been notified immediately. If you are in danger, please call emergency services.</p>
            <Button onClick={() => setRedFlagResult(null)} variant="outline" className="mt-3 border-[#C85C5C]/30 text-[#C85C5C] text-xs">Understood</Button>
          </div>
        )}

        {/* HOME VIEW */}
        {view === 'home' && (
          <>
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans'] mb-2">Support &amp; Guidance</p>
              <h1 className="text-xl sm:text-2xl font-['Outfit'] font-light text-[#2C2A29]">What does your system need help interpreting?</h1>
              <p className="text-xs text-[#8A8681] font-['Work_Sans'] mt-1">Select a category. Your observation will be reviewed in context of your biological journey.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-8" data-testid="category-grid">
              {categories.map(cat => {
                const Icon = ICONS[cat.icon] || MessageSquare;
                const isUrgent = cat.key === 'urgent';
                return (
                  <button key={cat.key} onClick={() => selectCategory(cat)}
                    className={`text-left p-4 rounded-2xl border transition-all hover:shadow-md ${
                      isUrgent ? 'border-[#C85C5C]/20 bg-[#C85C5C]/[0.03] hover:bg-[#C85C5C]/[0.06]' : 'border-[#EBE8E3] hover:bg-[#F8F6F3]'
                    }`} data-testid={`category-${cat.key}`}>
                    <Icon className={`w-5 h-5 mb-2 ${isUrgent ? 'text-[#C85C5C]' : 'text-[#8C6B55]'}`} strokeWidth={1.25} />
                    <p className={`text-sm font-['Work_Sans'] font-medium ${isUrgent ? 'text-[#C85C5C]' : 'text-[#2C2A29]'}`}>{cat.label}</p>
                  </button>
                );
              })}
            </div>

            {/* Active tickets */}
            {tickets.filter(t => t.status !== 'resolved' && t.status !== 'closed').length > 0 && (
              <div className="mb-6">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Active Requests</p>
                <div className="space-y-2">
                  {tickets.filter(t => t.status !== 'resolved' && t.status !== 'closed').map(t => (
                    <button key={t.id} onClick={() => openThread(t)}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-[#EBE8E3] hover:bg-[#F8F6F3] transition-colors" data-testid={`ticket-${t.id}`}>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: TRIAGE_COLORS[t.triage_level] || '#8A8681' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2C2A29] font-['Work_Sans'] truncate capitalize">{t.category?.replace('_', ' ')}</p>
                        <p className="text-[10px] text-[#8A8681]">{new Date(t.created_at).toLocaleDateString()} &middot; {t.status.replace('_', ' ')}</p>
                      </div>
                      <Badge variant="outline" className="text-[8px]" style={{ borderColor: (TRIAGE_COLORS[t.triage_level] || '#8A8681') + '30', color: TRIAGE_COLORS[t.triage_level] }}>
                        {t.triage_level?.replace('_', ' ')}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-[#8A8681]" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Resolved */}
            {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Resolved</p>
                {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').slice(0, 5).map(t => (
                  <button key={t.id} onClick={() => openThread(t)} className="w-full text-left flex items-center gap-3 py-2 text-[#8A8681]">
                    <span className="text-xs font-['Work_Sans'] capitalize flex-1">{t.category?.replace('_', ' ')}</span>
                    <span className="text-[10px]">{new Date(t.created_at).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* FORM VIEW */}
        {view === 'form' && selectedCategory && (
          <div data-testid="support-form">
            <button onClick={() => setView('home')} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-6 hover:text-[#5C5955]">
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            <h2 className="text-lg font-['Outfit'] font-light text-[#2C2A29] mb-1 capitalize">{selectedCategory.label}</h2>
            <p className="text-xs text-[#8A8681] font-['Work_Sans'] mb-6">Fill in the details so your care team can respond accurately.</p>

            <div className="space-y-4">
              {formSchema.map(field => (
                <div key={field.key}>
                  <label className="text-xs text-[#5C5955] font-['Work_Sans'] mb-1.5 block">
                    {field.label} {field.required && <span className="text-[#C87B7B]">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <Textarea value={formData[field.key] || ''} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      className="bg-white border-[#EBE8E3] text-[#2C2A29] text-sm min-h-[80px]" data-testid={`field-${field.key}`} />
                  ) : field.type === 'select' ? (
                    <Select value={formData[field.key] || ''} onValueChange={v => setFormData(p => ({ ...p, [field.key]: v }))}>
                      <SelectTrigger className="bg-white border-[#EBE8E3] text-[#2C2A29]" data-testid={`field-${field.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>{field.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : field.type === 'number' ? (
                    <Input type="number" min={field.min} max={field.max} value={formData[field.key] || ''} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      className="bg-white border-[#EBE8E3] text-[#2C2A29]" data-testid={`field-${field.key}`} />
                  ) : (
                    <Input value={formData[field.key] || ''} onChange={e => setFormData(p => ({ ...p, [field.key]: e.target.value }))}
                      className="bg-white border-[#EBE8E3] text-[#2C2A29]" data-testid={`field-${field.key}`} />
                  )}
                </div>
              ))}

              <div>
                <label className="text-xs text-[#5C5955] font-['Work_Sans'] mb-1.5 block">Additional message</label>
                <Textarea value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Anything else your care team should know..." className="bg-white border-[#EBE8E3] text-[#2C2A29] text-sm min-h-[60px]" data-testid="additional-message" />
              </div>

              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full py-5" data-testid="submit-support">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Submit Request</>}
              </Button>
            </div>
          </div>
        )}

        {/* THREAD VIEW */}
        {view === 'thread' && activeTicket && (
          <div data-testid="thread-view">
            <button onClick={() => { setView('home'); setActiveTicket(null); }} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-4 hover:text-[#5C5955]">
              <ChevronLeft className="w-3 h-3" /> Back
            </button>

            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-['Outfit'] text-[#2C2A29] capitalize">{activeTicket.category?.replace('_', ' ')}</h2>
                <p className="text-[10px] text-[#8A8681]">{new Date(activeTicket.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[9px]" style={{ borderColor: (TRIAGE_COLORS[activeTicket.triage_level] || '#8A8681') + '30', color: TRIAGE_COLORS[activeTicket.triage_level] }}>
                  {activeTicket.triage_level?.replace('_', ' ')}
                </Badge>
                <Badge variant="outline" className="text-[9px] border-[#EBE8E3] text-[#8A8681] capitalize">{activeTicket.status?.replace('_', ' ')}</Badge>
              </div>
            </div>

            {/* Messages */}
            <div className="space-y-3 mb-6">
              {threadMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'subscriber' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.id}`}>
                  <div className={`max-w-[80%] rounded-2xl p-3.5 ${
                    msg.sender_type === 'subscriber' ? 'bg-[#2C2A29] text-white' :
                    msg.message_type === 'auto_summary' ? 'bg-[#F4F2EE] text-[#5C5955] border border-[#EBE8E3]' :
                    'bg-[#4E7A6B]/10 text-[#2C2A29] border border-[#4E7A6B]/10'
                  }`}>
                    {msg.sender_type !== 'subscriber' && (
                      <p className="text-[9px] font-medium mb-1 opacity-60">{msg.sender_name || msg.sender_type}</p>
                    )}
                    <p className="text-sm font-['Work_Sans'] leading-relaxed whitespace-pre-line">{msg.message_text}</p>
                    <p className="text-[9px] opacity-40 mt-1">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Reply */}
            {activeTicket.status !== 'resolved' && activeTicket.status !== 'closed' && (
              <div className="flex gap-2">
                <Textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Reply..."
                  className="bg-white border-[#EBE8E3] text-[#2C2A29] text-sm min-h-[50px] flex-1" data-testid="reply-input" />
                <Button onClick={sendReply} disabled={!replyText.trim()} className="bg-[#2C2A29] text-white self-end px-4" data-testid="send-reply">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </BiologicalLayout>
  );
}
